import { BusinessDocType, IDocument, IInvoiceContact } from '../../legacy/transcepta-types';
import {
    IInvoiceMiscAmount,
    IInvoiceLineItemMiscAmount,
    IInvoiceCompanyGLAccount,
    IInvoiceLineItemCompanyGLAccount,
    IInvoiceShippingDetail,
    IInvoiceLineItemShippingDetail,
} from '../../services/Document/Api';

const isBlankOrEmptyMiscAmount = ({
    Amount,
    Quantity,
    Rate,
    UnitPrice,
}: IInvoiceMiscAmount | IInvoiceLineItemMiscAmount) => !Amount && !Quantity && !Rate && !UnitPrice;

const isEmptyMiscAmount = ({ Amount, Quantity, Rate, UnitPrice }: IInvoiceMiscAmount | IInvoiceLineItemMiscAmount) =>
    Amount === null && Quantity === null && Rate === null && UnitPrice === null;

const replaceEmptyStringWithZero = <T>(v: T) => (typeof v === 'string' && v === '' ? 0 : v);

/**
 * Replaces the empty string set by the Document Display logic.
 * Sending empty strings causes the API to set these values to null,
 * that would then show up as NaN in FleetPride's reports.
 * See: http://azu-dev01:8080/tfs/TR_Dev_Collection/Apollo/_workitems/edit/6916
 */
const correctMiscAmountValues = <TMiscAmount extends IInvoiceMiscAmount | IInvoiceLineItemMiscAmount>(
    miscAmount: TMiscAmount
): TMiscAmount => {
    const { Amount, Quantity, Rate, UnitPrice } = miscAmount;

    return {
        ...miscAmount,
        Amount: replaceEmptyStringWithZero(Amount),
        Quantity: replaceEmptyStringWithZero(Quantity),
        Rate: replaceEmptyStringWithZero(Rate),
        UnitPrice: replaceEmptyStringWithZero(UnitPrice),
    };
};

/**
 * CompanyGLAccount is blank or empty is price/percentage are 0 AND coding only has blank or empty values for sub objects.
 */
const isBlankOrEmptyCompanyGLAccount = ({
    PriceApplied,
    PercentageApplied,
    Coding,
}: IInvoiceCompanyGLAccount | IInvoiceLineItemCompanyGLAccount) => {
    const price = Number(PriceApplied);
    const percentage = Number(PercentageApplied);
    const isValidNumber = (num: number) => !isNaN(num) && num !== 0;
    const hasValidAmounts = isValidNumber(price) || isValidNumber(percentage);

    if (!Coding?.Coding) {
        return true;
    }

    const hasNonEmptyCodingValues = Object.values(Coding.Coding)
        .filter((field) => typeof field === 'object' && field !== null)
        .some((field: any) => {
            // Skip metadata fields like @id
            if (typeof field !== 'object' || !field.value) {
                return false;
            }
            return field.value !== '' && field.value !== null;
        });

    return !hasValidAmounts && !hasNonEmptyCodingValues;
};

const isEmptyCompanyGLAccount = ({
    PriceApplied,
    PercentageApplied,
}: IInvoiceCompanyGLAccount | IInvoiceLineItemCompanyGLAccount) => {
    if (PriceApplied === null && PercentageApplied === null) {
        return true;
    }

    const price = Number(PriceApplied);
    const percentage = Number(PercentageApplied);

    const isValid = (num: number) => !isNaN(num);

    return !isValid(price) && !isValid(percentage);
};

// Checks if all relevant values are falsy
const isEmptyShippingDetail = (shippingDetails: IInvoiceShippingDetail | IInvoiceLineItemShippingDetail) =>
    Object.entries(shippingDetails)
        .filter(([key]) => !['InvoiceLineItemId', 'Id', 'LastUpdateDate', 'Rowversion', 'InvoiceId'].includes(key))
        .every(([, value]) => !value);

const isEmptyContact = (contact: IInvoiceContact): boolean => {
    const significantFields = [
        'Name',
        'Contact',
        'PartyId',
        'Address1',
        'Address2',
        'Address3',
        'City',
        'State',
        'PostalCode',
        'Country',
        'AddressBlock',
        'PhoneNumber',
        'Email',
        'Address4',
        'FaxNumber',
    ] as (keyof IInvoiceContact)[];

    return significantFields.every((field) => !contact[field]);
};

// TODO: remove flag when feature fully deployed
export function removeEmptyEntitiesFromDocument(
    doc: IDocument,
    shouldCleanDocumentForSave: boolean = false
): IDocument {
    switch (doc.BusinessDocType) {
        case BusinessDocType.Invoice: {
            const invoice = doc.BusinessDocFields.Invoice;

            return {
                ...doc,
                BusinessDocFields: {
                    ...doc.BusinessDocFields,
                    Invoice: {
                        ...invoice,
                        InvoiceContacts: shouldCleanDocumentForSave
                            ? invoice.InvoiceContacts?.filter((contact) => !isEmptyContact(contact)) ?? []
                            : invoice.InvoiceContacts ?? [],

                        // Remove Empty Company GL Accounts
                        InvoiceCompanyGLAccounts: shouldCleanDocumentForSave
                            ? invoice.InvoiceCompanyGLAccounts.filter(
                                  (glAccount) => !isBlankOrEmptyCompanyGLAccount(glAccount)
                              )
                            : invoice.InvoiceCompanyGLAccounts.filter(
                                  (glAccount) => !isEmptyCompanyGLAccount(glAccount)
                              ),
                        // Remove Empty Misc Amounts
                        InvoiceMiscAmounts: shouldCleanDocumentForSave
                            ? invoice.InvoiceMiscAmounts.filter((miscAmount) => !isBlankOrEmptyMiscAmount(miscAmount))
                            : invoice.InvoiceMiscAmounts.filter((miscAmount) => !isEmptyMiscAmount(miscAmount)).map(
                                  correctMiscAmountValues
                              ),
                        // Remove Empty Shipping Details
                        InvoiceShippingDetails: invoice.InvoiceShippingDetails.filter(
                            (detail) => !isEmptyShippingDetail(detail)
                        ),
                        InvoiceLineItems: invoice.InvoiceLineItems.map((line) => ({
                            ...line,
                            InvoiceLineItemCompanyGLAccounts: shouldCleanDocumentForSave
                                ? line.InvoiceLineItemCompanyGLAccounts.filter(
                                      (glAccount) => !isBlankOrEmptyCompanyGLAccount(glAccount)
                                  )
                                : line.InvoiceLineItemCompanyGLAccounts.filter(
                                      (glAccount) => !isEmptyCompanyGLAccount(glAccount)
                                  ),
                            InvoiceLineItemMiscAmounts: shouldCleanDocumentForSave
                                ? line.InvoiceLineItemMiscAmounts.filter(
                                      (miscAmount) => !isBlankOrEmptyMiscAmount(miscAmount)
                                  )
                                : line.InvoiceLineItemMiscAmounts.filter(
                                      (miscAmount) => !isEmptyMiscAmount(miscAmount)
                                  ).map(correctMiscAmountValues),
                            InvoiceLineItemShippingDetails: line.InvoiceLineItemShippingDetails.filter(
                                (detail) => !isEmptyShippingDetail(detail)
                            ),
                        })),
                    },
                },
            };
        }
        case BusinessDocType.PurchaseOrderAcknowledgement: {
            const poa = doc.BusinessDocFields.BusinessDocument.Document.PurchaseOrderAcknowledgement;
            return {
                ...doc,
                BusinessDocFields: {
                    ...doc.BusinessDocFields,
                    BusinessDocument: {
                        ...doc.BusinessDocFields.BusinessDocument,
                        Document: {
                            ...doc.BusinessDocFields.BusinessDocument.Document,
                            PurchaseOrderAcknowledgement: {
                                ...poa,
                                MiscAmounts: {
                                    ...(poa?.MiscAmounts ?? {}),
                                    MiscAmount: poa?.MiscAmounts?.MiscAmount?.filter?.(
                                        (miscAmount: any) => !isEmptyMiscAmount(miscAmount)
                                    ),
                                },
                            },
                        },
                    },
                },
            };
        }
        default: {
            return doc;
        }
    }
}
